import { Alert, Anchor, Badge, Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { explainFunctionError, functionErrorResponse, useSupabase, useTenant } from "@how/app-kit";
import { semanticColor, type SemanticRole } from "@how/design-system";

/**
 * Where the church's Stripe account stands (supabase/functions/_shared/stripe-status.ts). After Stripe's form, three
 * situations look alike from the outside and only one asks anything of the church (owner, 2026-10-02): Stripe is
 * reviewing (wait), Stripe needs more information (continue in Stripe), or Stripe has paused the account (see the
 * Stripe Dashboard).
 */
type Status =
  "not_started" | "in_progress" | "reviewing" | "needs_info" | "restricted" | "complete";

interface PaymentStatus {
  readonly status: Status;
  readonly chargesEnabled?: boolean;
  readonly payoutsEnabled?: boolean;
  readonly accountId?: string;
}

const LABEL: Record<Status, string> = {
  not_started: "Not set up",
  in_progress: "Set-up in progress",
  reviewing: "Stripe is reviewing your details",
  needs_info: "Stripe needs more information",
  restricted: "Payments paused by Stripe",
  complete: "Ready to take payments",
};
const ROLE: Record<Status, SemanticRole> = {
  not_started: "neutral",
  in_progress: "info",
  reviewing: "info",
  needs_info: "warning",
  restricted: "danger",
  complete: "success",
};
/** What the church does next, in a sentence; null when the badge says it all. */
const NEXT: Record<Status, string | null> = {
  not_started: null,
  in_progress: "Finish the steps on Stripe's pages to start taking payments.",
  reviewing:
    "Nothing to do: Stripe usually finishes in a few minutes, sometimes a day or two. This updates on its own.",
  needs_info:
    "Stripe needs a little more from you before payments can start. Continue on Stripe's pages.",
  restricted:
    "Stripe has paused this account. Open your Stripe Dashboard to see why and what it needs.",
  complete: null,
};
/** Statuses where going back to Stripe's onboarding pages is the next step. */
const CONTINUE: ReadonlySet<Status> = new Set(["not_started", "in_progress", "needs_info"]);

/**
 * Payments (ADR 0009): the church's own Stripe account, connected under the platform's, with the church as merchant
 * of record. Set-up happens on Stripe's own pages; this card starts it, resumes it, and shows where it stands.
 */
export function PaymentsCard() {
  const supabase = useSupabase();
  const tenant = useTenant();
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : "";
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const returned = params.get("payments");

  const status = useQuery({
    queryKey: ["payment-account", tenantId],
    enabled: tenantId !== "",
    queryFn: async (): Promise<PaymentStatus> => {
      const { data, error } = await supabase.rpc("payment_account_status", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      return data as unknown as PaymentStatus;
    },
  });

  const connect = useMutation({
    mutationFn: async (action: "start" | "sync"): Promise<{ status: Status; url?: string }> => {
      const result = await supabase.functions.invoke<{ status: Status; url?: string }>(
        "stripe-connect",
        {
          body: { tenant_id: tenantId, action },
        },
      );
      if (result.error) {
        throw await explainFunctionError(result.error, functionErrorResponse(result.error));
      }
      return result.data ?? { status: "not_started" };
    },
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: ["payment-account", tenantId] });
      if (result.url !== undefined) window.location.assign(result.url);
    },
  });

  // Back from Stripe: read the account once, then drop the marker from the address.
  useEffect(() => {
    if (returned === null || tenantId === "") return;
    connect.mutate("sync");
    setParams((current) => {
      current.delete("payments");
      return current;
    });
  }, [returned, tenantId]);

  const current = status.data?.status ?? "not_started";
  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={4}>Payments</Title>
            <Text size="sm" c="dimmed" maw={560}>
              Event fees and the store are paid into your church's own Stripe account. Stripe
              handles the identity and bank details on its own pages; the church is the merchant of
              record and DCX Logic never holds the money.
            </Text>
          </div>
          <Badge variant="light" color={semanticColor(ROLE[current])} style={{ flexShrink: 0 }}>
            {LABEL[current]}
          </Badge>
        </Group>
        {NEXT[current] === null ? null : (
          <Text size="sm" data-testid="payments-next">
            {NEXT[current]}
            {current === "restricted" ? (
              <>
                {" "}
                <Anchor href="https://dashboard.stripe.com" target="_blank" rel="noreferrer">
                  Open the Stripe Dashboard (opens in a new tab)
                </Anchor>
              </>
            ) : null}
          </Text>
        )}
        {status.data?.accountId === undefined ? null : (
          <Text size="xs" c="dimmed">
            Stripe account {status.data.accountId}
            {status.data.chargesEnabled === true
              ? ", taking payments"
              : ", not yet taking payments"}
            {status.data.payoutsEnabled === true ? ", paying out." : ", not yet paying out."}
          </Text>
        )}
        {connect.isError ? (
          <Alert color={semanticColor("danger")} variant="light">
            {connect.error.message}
          </Alert>
        ) : null}
        <Group>
          {!CONTINUE.has(current) ? null : (
            <Button
              loading={connect.isPending}
              onClick={() => {
                connect.mutate("start");
              }}
            >
              {current === "not_started"
                ? "Set up payments with Stripe"
                : "Continue set-up in Stripe"}
            </Button>
          )}
          {current === "not_started" ? null : (
            <Button
              variant="subtle"
              loading={connect.isPending}
              onClick={() => {
                connect.mutate("sync");
              }}
            >
              Check status
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
